// Lib
import { FC, useEffect, useState } from "react";
import { Form } from "antd";
// Api
import {
  useCreateAuthServiceSettingsListOptionMutation,
  useGetAuthServiceSettingsListOptionsQuery,
  useRemoveAuthServiceSettingsListOptionMutation,
  useUpdateAuthServiceSettingsListOptionMutation,
} from "rtkQuery/query/authSettingsAPI";
// Hooks
import { useNotification, useViewport } from "hooks";
// Theme
import { theme } from "theme";
// Constants
import { rtkQueryParams } from "consts";
// Helpers
import { generateUniqueId } from "helpers/dataHelpers";
// Utils
import { errorHandler } from "utils/errorHandler";
// Icons
import {
  CloseBlackIcon,
  DoneBlackIcon,
  OrdersIcon,
  PlusIcon,
  TrashIcon,
} from "icons";
// Components
import { ButtonLink, Table } from "components";
import { Input, InputNumber } from "components/Form";
import { ConfirmDialog } from "components/Modals";
// Styled
import { ContentBox, FlexContainer } from "styled/Box";
import { Button } from "styled/Buttons";
import { Typography } from "styled/Typography";
import { SettingsListOption } from "types/authSettings";

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  inputType: "number" | "text";
  record: any;
  index: number;
  children: React.ReactNode;
}

const EditableCell: React.FC<EditableCellProps> = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}) => {
  const inputNode = inputType === "number" ? <InputNumber /> : <Input />;

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[
            {
              required: true,
              message: `${title} is required`,
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

type ColumnsData = {
  name: string;
  value: string;
  key: string;
};

export const ServiceTable: FC = () => {
  const { isMobile } = useViewport();

  const { openNotification } = useNotification();

  const [form] = Form.useForm();

  const [data, setData] = useState<ColumnsData[]>([]);

  const [editingKey, setEditingKey] = useState("");

  const [confirmModal, setConfirmModal] = useState<ColumnsData | null>(null);

  const {
    data: optionsData,
    isFetching: isTableDataLoading,
    error: optionsError,
  } = useGetAuthServiceSettingsListOptionsQuery(
    { type: SettingsListOption.service },
    rtkQueryParams,
  );
  const [create, { isLoading: isCreateOptionLoading }] =
    useCreateAuthServiceSettingsListOptionMutation();
  const [update, { isLoading: isUpdateOptionLoading }] =
    useUpdateAuthServiceSettingsListOptionMutation();
  const [remove, { isLoading: isRemoveOptionLoading }] =
    useRemoveAuthServiceSettingsListOptionMutation();

  useEffect(() => {
    if (optionsData) {
      setData(
        optionsData.map(({ name, id }) => ({ value: name, name: id, key: id })),
      );
    }
  }, [optionsData]);

  useEffect(() => {
    if (optionsError) {
      errorHandler({ error: optionsError, openNotification });
    }
  }, [optionsError]);

  const isEditing = (record: ColumnsData) => record.key === editingKey;

  const handleEdit = (record: ColumnsData) => {
    form.setFieldsValue(record);

    setEditingKey(record.key);
  };

  const onCancel = () => {
    if (editingKey.includes("temp")) {
      setData(prev => prev.filter(item => item.key !== editingKey));
    }
    setEditingKey("");
  };

  const onSave = async (key: React.Key) => {
    try {
      const row = await form.validateFields();

      if (!row.value) return;
      const newData = [...data];

      if (key.toString().includes("temp")) {
        await create({
          name: row.value,
          type: SettingsListOption.service,
        }).unwrap();
      } else {
        await update({
          id: key.toString(),
          name: row.value,
          type: SettingsListOption.service,
        }).unwrap();
      }
      const index = newData.findIndex(item => key === item?.key);

      if (index > -1) {
        const item = newData[index];
        newData.splice(index, 1, {
          ...item,
          ...row,
        });
        setData(newData);
        setEditingKey("");
      } else {
        newData.push(row);
        setData(newData);
        setEditingKey("");
      }
    } catch (error) {
      if ("values" in error && "errorFields" in error) {
        return;
      }
      errorHandler({ error, openNotification });
    }
  };

  const handleAddOption = () => {
    const id = `temp-${generateUniqueId()}`;

    form.setFieldsValue({ name: id, value: "" });

    setEditingKey(id);
    setData(prev => [{ value: "", name: id, key: id }, ...prev]);
  };

  const onConfirmRemove = async () => {
    if (!confirmModal) return;

    try {
      await remove({ id: confirmModal.key }).unwrap();

      setConfirmModal(null);
    } catch (error) {
      errorHandler({ error, openNotification });
    }
  };

  const onCancelRemove = () => {
    setConfirmModal(null);
  };

  const columns = [
    {
      title: "name",
      dataIndex: "value",
      editable: true,
      render: (text: string) => (
        <FlexContainer $align="center" $height="40px" $padding="0 13px">
          {text}
        </FlexContainer>
      ),
    },
    {
      title: "operation",
      dataIndex: "operation",
      align: "right" as const,
      render: (_: any, record: ColumnsData) => {
        const editable = isEditing(record);
        return editable ? (
          <FlexContainer $gap={8} $align="center" $justify="flex-end">
            <Button.SquaredIcon
              type="primary"
              loading={isCreateOptionLoading || isUpdateOptionLoading}
              icon={<DoneBlackIcon fill={theme.color.white} />}
              onClick={() => onSave(record.key)}
            />

            <Button.CloseSquaredIcon
              icon={<CloseBlackIcon />}
              disabled={isCreateOptionLoading || isUpdateOptionLoading}
              onClick={onCancel}
            />
          </FlexContainer>
        ) : (
          <FlexContainer $gap={8} $align="center" $justify="flex-end">
            <ButtonLink
              title="Edit"
              disabled={editingKey !== ""}
              onClick={() => handleEdit(record)}
            />

            <ButtonLink
              title="Remove"
              disabled={editingKey !== ""}
              onClick={() => setConfirmModal(record)}
            />
          </FlexContainer>
        );
      },
    },
  ];

  const mergedColumns = columns.map(col => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: ColumnsData) => ({
        record,
        inputType: "text",
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  return (
    <>
      <ContentBox $column $gap={12}>
        <FlexContainer $fullwidth $align="flex-start" $justify="space-between">
          <Typography.H2>Orders Source Options</Typography.H2>

          {isMobile ? (
            <Button.SquaredIcon
              type="primary"
              icon={<PlusIcon fill="white" />}
              onClick={handleAddOption}
              disabled={editingKey !== ""}
            />
          ) : (
            <Button.Base
              type="primary"
              icon={<PlusIcon fill="white" />}
              onClick={handleAddOption}
              disabled={editingKey !== ""}
            >
              Add Source
            </Button.Base>
          )}
        </FlexContainer>

        <Form form={form} component={false}>
          <Table
            rowKey={"key"}
            loading={isTableDataLoading}
            shadow={false}
            showHeader={false}
            nested={true}
            components={{
              body: {
                cell: EditableCell,
              },
            }}
            empty={{
              icon: OrdersIcon,
              title: "No services to show.",
            }}
            dataSource={data}
            columns={mergedColumns}
          />
        </Form>
      </ContentBox>

      <ConfirmDialog
        open={!!confirmModal}
        Icon={TrashIcon}
        isLoading={isRemoveOptionLoading}
        message={confirmModal && `${confirmModal?.value} will be removed`}
        description="Are you sure to continue this action?"
        onCancel={onCancelRemove}
        firstCTAButton={{
          title: "Accept",
          status: "danger",
          disabled: false,
          loading: isRemoveOptionLoading,
          onClick: onConfirmRemove,
        }}
      />
    </>
  );
};
